import React from "react";

import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import EngineeringUserYearRow from "./EngineeringUserYearRow.jsx";

/**
 * Calculates and displays the percentage of time that a user worked on each project
 * in a given year, including accounting for time not associated with any project.
 *
 * @param {object} props - The component props.
 * @param {object} props.user - The user data containing work history.
 * @returns {React.ReactElement} The rendered component.
 */
function EngineeringUserProjectDistribution({ user }) {
  const theme = useTheme();

  //---------------------------------------------------------------------------
  // Data Processing
  //---------------------------------------------------------------------------
  const { years, projectStatsByYear } = React.useMemo(() => {
    const statsByYear = new Map();
    const availableYears = new Set();
    const currentYear = DateTime.now().year;

    // Ensure current year exists in the map
    statsByYear.set(currentYear, { totalHours: 0, projects: new Map(), unassigned: 0 });
    availableYears.add(currentYear);

    user.userWork?.forEach((work) => {
      if (!work.sprint?.endDate) {
        return;
      }

      const endDate = DateTime.fromISO(work.sprint.endDate);
      const year = endDate.year;

      availableYears.add(year);

      if (!statsByYear.has(year)) {
        statsByYear.set(year, { totalHours: 0, projects: new Map(), unassigned: 0 });
      }

      const yearStats = statsByYear.get(year);
      const hours = Number(work.hours) || 0;
      yearStats.totalHours += hours;

      if (work.project) {
        const projectId = work.project.id;
        if (!yearStats.projects.has(projectId)) {
          yearStats.projects.set(projectId, {
            id: projectId,
            name: work.project.name,
            jiraId: work.project.jiraId,
            hours: 0,
          });
        }
        const project = yearStats.projects.get(projectId);
        project.hours += hours;
      } else {
        yearStats.unassigned += hours;
      }
    });

    return {
      years: Array.from(availableYears).toSorted((a, b) => b - a),
      projectStatsByYear: statsByYear,
    };
  }, [user.userWork]);

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <Box sx={{ mx: 2 }}>
      <Typography variant="h6" align="center" gutterBottom>
        Work Distribution
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size="auto">
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
            }}
          >
            Year
          </Typography>
        </Grid>
        <Grid size="grow">
          <Grid container spacing={2}>
            <Grid size={{ xs: "grow", md: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Project
              </Typography>
            </Grid>
            <Grid size="grow" sx={{ display: { xs: "none", md: "block" } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Distribution
              </Typography>
            </Grid>
            <Grid size={{ xs: 4, md: 2 }}>
              <Typography
                variant="subtitle2"
                align="center"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Percentage
              </Typography>
            </Grid>
            <Grid size={2} sx={{ display: { xs: "none", md: "block" } }}>
              <Typography
                variant="subtitle2"
                align="center"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Hours
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider variant="middle" color={theme.palette.primary.dark} sx={{ borderWidth: "1px", mx: -1 }} />
      {years.map((year) => (
        <EngineeringUserYearRow key={year} year={year} stats={projectStatsByYear.get(year)} user={user} />
      ))}
    </Box>
  );
}

export default EngineeringUserProjectDistribution;
